import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { Helmet } from "react-helmet-async";
import { Building2, Users, Scale, Shield, UserCheck, Landmark, BookOpen } from "lucide-react";

const cariche = [
  {
    ruolo: "Presidente",
    nome: "Massimiliano Ricci",
    descrizione: "Rappresenta legalmente l'Associazione. Convoca e presiede il Consiglio Direttivo e l'Assemblea, cura l'esecuzione delle deliberazioni e sovrintende all'attività sociale e sportiva nel rispetto dello Statuto e delle norme federali.",
    icon: Landmark,
  },
  {
    ruolo: "Vicepresidente",
    nome: "Eun Jae Oh",
    descrizione: "Sostituisce il Presidente in caso di assenza o impedimento e lo coadiuva nello svolgimento delle funzioni istituzionali e organizzative.",
    icon: UserCheck,
  },
  {
    ruolo: "Segretario",
    nome: "Silvio Bencivenga",
    descrizione: "Cura la redazione dei verbali degli organi sociali, la gestione della corrispondenza e l'esecuzione delle deliberazioni adottate.",
    icon: BookOpen,
  },
  {
    ruolo: "Tesoriere",
    nome: "Leonardo Caltagirone",
    descrizione: "Gestisce l'amministrazione e la contabilità dell'Associazione, cura le riscossioni e i pagamenti secondo mandato del Consiglio Direttivo e garantisce la regolare tenuta dei libri contabili.",
    icon: Scale,
  },
  {
    ruolo: "Consigliere",
    nome: "Fabio Lalli",
    descrizione: "Partecipa alle attività del Consiglio Direttivo contribuendo alle decisioni strategiche, organizzative e di sviluppo dell'Associazione.",
    icon: Users,
  },
];

const organi = [
  "L'Assemblea Generale degli Associati",
  "Il Presidente",
  "Il Consiglio Direttivo",
  "L'Organo di Revisione, ove istituito",
];

const Asd = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    name: "A.S.D. Taekwondo Mattei Roma",
    url: "https://taekwondomattei.com/asd",
    taxID: "97831630583",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via Forno Saraceno, 129",
      addressLocality: "Roma",
      addressRegion: "RM",
      addressCountry: "IT",
    },
    memberOf: {
      "@type": "SportsOrganization",
      name: "Federazione Italiana Taekwondo",
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>A.S.D. Taekwondo Mattei Roma – Informazioni Istituzionali</title>
        <meta name="description" content="Informazioni istituzionali dell'A.S.D. Taekwondo Mattei Roma: natura giuridica, finalità, organi sociali, cariche in carica e principi di trasparenza." />
        <link rel="canonical" href="https://taekwondomattei.com/asd" />
        <meta property="og:title" content="A.S.D. Taekwondo Mattei Roma – Informazioni Istituzionali" />
        <meta property="og:description" content="Scopri la struttura istituzionale, le finalità e le cariche sociali dell'Associazione Sportiva Dilettantistica Taekwondo Mattei Roma." />
        <meta property="og:url" content="https://taekwondomattei.com/asd" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="A.S.D. Taekwondo Mattei Roma – Informazioni Istituzionali" />
        <meta name="twitter:description" content="Scopri la struttura istituzionale, le finalità e le cariche sociali dell'Associazione Sportiva Dilettantistica Taekwondo Mattei Roma." />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <SeoBreadcrumb items={[{ label: "A.S.D." }]} />

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden gradient-black">
        <div className="relative z-10 container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gold mb-6">
              A.S.D. Taekwondo Mattei Roma
            </h1>
            <p className="font-body text-xl text-gold/80">
              Associazione Sportiva Dilettantistica – Informazioni istituzionali, struttura e trasparenza.
            </p>
          </div>
        </div>
      </section>

      {/* Presentazione */}
      <section className="section-padding bg-background">
        <div className="container-narrow mx-auto">
          <SectionHeader title="Presentazione" subtitle="Natura giuridica e affiliazione" />
          <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
            <p>
              A.S.D. Taekwondo Mattei Roma è un'associazione sportiva dilettantistica di diritto privato, apolitica e senza fini di lucro, costituita ai sensi del Codice Civile e del D.Lgs. 36/2021. L'Associazione opera nel rispetto dei principi di democraticità, uguaglianza dei diritti tra gli associati e assenza di scopo di lucro, con obbligo di reinvestimento degli eventuali avanzi di gestione per il perseguimento delle finalità istituzionali.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-elevated p-6 border border-border flex items-start gap-4">
                <Building2 className="h-6 w-6 text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-sm font-semibold text-foreground mb-1">Sede legale</h3>
                  <p className="font-body text-sm text-muted-foreground">Via Forno Saraceno, 129 – Roma</p>
                </div>
              </div>
              <div className="card-elevated p-6 border border-border flex items-start gap-4">
                <Shield className="h-6 w-6 text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-display text-sm font-semibold text-foreground mb-1">Codice Fiscale</h3>
                  <p className="font-body text-sm text-muted-foreground">97831630583</p>
                </div>
              </div>
            </div>
            <p>
              L'Associazione è affiliata alla <strong className="text-foreground">Federazione Italiana Taekwondo</strong>, impegnandosi al rispetto delle norme e delle direttive dell'ordinamento sportivo nazionale e internazionale.
            </p>
          </div>
        </div>
      </section>

      {/* Finalità e missione */}
      <section className="section-padding bg-secondary">
        <div className="container-narrow mx-auto">
          <SectionHeader title="Finalità e missione" subtitle="Attività sportive dilettantistiche e formazione" />
          <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
            <p>
              L'Associazione esercita in via stabile e principale l'organizzazione e la gestione di attività sportive dilettantistiche connesse alla disciplina del Taekwondo, quale strumento di formazione psico-fisica e morale degli associati.
            </p>
            <p>
              Le attività comprendono l'avviamento alla pratica sportiva, la formazione tecnica, la preparazione agonistica, la partecipazione a competizioni e manifestazioni federali, nonché ogni iniziativa idonea alla diffusione della disciplina, nel rispetto delle normative vigenti.
            </p>
            <p>
              La missione dell'A.S.D. Taekwondo Mattei Roma è promuovere un ambiente sportivo strutturato, fondato su disciplina, rispetto delle regole, responsabilità individuale e crescita personale, garantendo qualità tecnica, sicurezza e trasparenza gestionale.
            </p>
          </div>
        </div>
      </section>

      {/* Struttura e organi sociali */}
      <section className="section-padding bg-background">
        <div className="container-narrow mx-auto">
          <SectionHeader title="Struttura e organi sociali" subtitle="Ordinamento interno e competenze" />
          <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
            <p>
              L'ordinamento interno si fonda su organi elettivi e su una chiara ripartizione di competenze, secondo quanto previsto dallo Statuto.
            </p>
            <p className="font-display text-base font-semibold text-foreground">
              Sono organi dell'Associazione:
            </p>
            <ul className="space-y-3 pl-2">
              {organi.map((organo) => (
                <li key={organo} className="flex items-start gap-3">
                  <span className="h-2 w-2 rounded-full bg-gold flex-shrink-0 mt-2.5" />
                  <span>{organo}</span>
                </li>
              ))}
            </ul>
            <p>
              L'Assemblea rappresenta l'organo sovrano dell'Associazione e delibera su bilancio, nomina e revoca degli organi sociali, modifiche statutarie e materie di propria competenza.
            </p>
            <p>
              Il Consiglio Direttivo è l'organo responsabile della gestione ordinaria e straordinaria dell'Associazione, con i più ampi poteri di amministrazione nel rispetto delle finalità statutarie. I componenti restano in carica quattro anni e sono rieleggibili.
            </p>
          </div>
        </div>
      </section>

      {/* Cariche sociali in carica */}
      <section className="section-padding bg-secondary">
        <div className="container-wide mx-auto">
          <SectionHeader title="Cariche sociali in carica" subtitle="Verbale del Consiglio Direttivo del 7 gennaio 2026" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cariche.map((carica) => {
              const Icon = carica.icon;
              return (
                <div key={carica.nome} className="card-elevated p-8 border border-border">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-full bg-gold/10">
                      <Icon className="h-6 w-6 text-gold" />
                    </div>
                    <div>
                      <p className="font-display text-sm font-semibold text-gold uppercase tracking-wider">
                        {carica.ruolo}
                      </p>
                      <h3 className="font-display text-lg font-bold text-foreground">
                        {carica.nome}
                      </h3>
                    </div>
                  </div>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {carica.descrizione}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Principi di trasparenza */}
      <section className="section-padding bg-background">
        <div className="container-narrow mx-auto">
          <SectionHeader title="Principi di trasparenza e conformità normativa" />
          <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
            <p>
              L'A.S.D. Taekwondo Mattei Roma garantisce la tenuta dei libri sociali obbligatori, la redazione annuale del bilancio e la messa a disposizione della documentazione agli associati, secondo quanto previsto dallo Statuto.
            </p>
            <p>
              L'Associazione opera nel rispetto della normativa sportiva vigente, delle disposizioni in materia di tutela dei minori, lavoro sportivo, volontariato e prevenzione di ogni forma di discriminazione o comportamento lesivo della dignità personale.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Asd;
